import React from 'react';

export default function TradingViewChart({
  symbol = 'COINBASE:BTCUSD',
  interval = '60',
  height = 750,
  autosize = true,
  theme = 'dark',
  onReady,
}) {
  const containerRef = React.useRef(null);
  // Keep a stable reference to the latest onReady without causing re-renders
  const onReadyRef = React.useRef(onReady);
  React.useEffect(() => { onReadyRef.current = onReady; }, [onReady]);

  React.useEffect(() => {
    if (!containerRef.current) return;

    // Clear container
    containerRef.current.innerHTML = '';

    // Create widget container
    const widgetContainer = document.createElement('div');
    widgetContainer.className = 'tradingview-widget-container';
    widgetContainer.style.height = '100%';
    widgetContainer.style.width = '100%';
    widgetContainer.style.paddingBottom = '10px';

    const widgetDiv = document.createElement('div');
    widgetDiv.className = 'tradingview-widget-container__widget';
    widgetDiv.style.height = 'calc(100% - 10px)';
    widgetDiv.style.width = '100%';

    // Create script with widget configuration - revert to embedded widget
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
    
    // Choose a sensible visible range based on interval to avoid TradingView auto-bumping to 1D
    // when intraday is requested with an excessively long range.
    const iv = String(interval || '').toUpperCase();
    let range = '12M';
  if (iv === '1') range = '1D'; // 1m -> 1 day (keep intraday resolution)
  else if (iv === '3') range = '5D'; // 3m -> 5 trading days
  else if (iv === '5') range = '5D'; // 5m -> 5 trading days
  else if (iv === '30') range = '1M';
  else if (iv === '60') range = '3M';
    else if (iv === '120' || iv === '180') range = '3M';
    else if (iv === '240') range = '6M';
    else if (iv === 'D' || iv === '1D') range = '12M';
    else range = '6M';

    // Configuration with volume hidden and time scale/interval forced
    const config = {
      autosize: true,
      symbol: symbol,
      interval: String(interval || '60'),
      timezone: "Etc/UTC",
      theme: theme,
      style: "1",
      locale: "en",
      hide_top_toolbar: false,
      hide_side_toolbar: false,
      allow_symbol_change: false,
      save_image: false,
      calendar: false,
      hide_volume: true, // Hide volume
      withdateranges: true,
      range: range,
      details: false, // Keep price sidebar, not market cap
      hotlist: false,
      enable_publishing: false,
      disabled_features: ["header_saveload"],
      enabled_features: ["study_templates"],
      overrides: {
        "scalesProperties.showSeriesLastValue": true,
        "scalesProperties.showRightScale": true,
        "scalesProperties.showLeftScale": false,
        "scalesProperties.fontSize": 12,
        "scalesProperties.showSymbolLabels": true,
        "scalesProperties.textColor": theme === 'dark' ? "#d1d4dc" : "#131722",
        "mainSeriesProperties.visible": true,
        "mainSeriesProperties.showCountdown": true,
        "paneProperties.topMargin": 15,
        "paneProperties.bottomMargin": 35
      },
      support_host: "https://www.tradingview.com"
    };

    script.innerHTML = JSON.stringify(config);

    widgetContainer.appendChild(widgetDiv);
    widgetContainer.appendChild(script);
    containerRef.current.appendChild(widgetContainer);

    // Mock ready callback for parent
    setTimeout(() => {
      const cb = onReadyRef.current;
      if (typeof cb === 'function') {
        const mockWidget = {
          chart: () => ({
            setSymbol: () => {},
            symbol: () => symbol
          })
        };
        cb(mockWidget);
      }
    }, 1000);

    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [symbol, interval, theme]);

  return <div ref={containerRef} style={{ height: autosize ? '100%' : height }} />;
}
